import type { MiceOrderView, VoucherView } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { IconButton, Spinner } from '@web/ui/core/atoms';
import { EmptyHint } from '@web/ui/core/molecules';
import { Drawer, VoucherDetailSections } from '@web/ui/core/organisms';
import { EmailCorrection } from './email_correction';
import { OrderStatusChip } from './order_status_chip';

interface OrderDrawerProps {
  order: MiceOrderView | null;
  /** Seção Cadastro do pedido de planilha solto (o de necessidade fica no painel dela). */
  profile?: React.ReactNode;
  vouchers: VoucherView[];
  /** Os outros aéreos vivos do viajante no período do pedido, fora os vinculados (#6007). */
  otherLive: VoucherView[];
  loading: boolean;
  /** A leitura dos vouchers falhou: não dá para afirmar que o voucher ainda não existe. */
  error: boolean;
  canPurchase: boolean;
  /** E-mail corrigido do pedido de planilha sem viajante; `onDone` fecha o campo. */
  onCorrectEmail: (orderId: string, email: string, onDone: () => void) => void;
  correctingEmail: boolean;
  onClose: () => void;
}

/** "Ida", "Volta" ou "Ida e volta", pelas direções do pedido que apontam para o voucher. */
function blockTitle(order: MiceOrderView, voucherId: string): string {
  const directions = new Set(
    order.vouchers.filter((v) => v.voucherId === voucherId).map((v) => v.direction),
  );
  if (directions.size > 1) return t('mice.drawer.roundTrip');
  return directions.has('RETURN') ? t('mice.drawer.inbound') : t('mice.drawer.outbound');
}

export function OrderDrawer({
  order,
  profile,
  vouchers,
  otherLive,
  loading,
  error,
  canPurchase,
  onCorrectEmail,
  correctingEmail,
  onClose,
}: Readonly<OrderDrawerProps>): React.JSX.Element {
  return (
    <Drawer
      open={order !== null}
      onClose={onClose}
      ariaLabel={t('mice.drawer.title', { row: order?.rowNumber ?? '' })}
    >
      {order ? (
        <>
          <div className="flex flex-shrink-0 items-center gap-3 border-b border-border-soft px-5 py-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="truncate font-serif text-drawer-title text-foreground">
                {order.travelerName ?? order.travelerEmail}
              </span>
              <span className="font-mono text-micro text-muted-foreground">
                {[order.outbound, order.inbound]
                  .filter((l) => l !== null)
                  .map((l) => `${l.carrier}${l.flightNumber} · ${l.date}`)
                  .join(' → ')}
              </span>
              {order.error ? (
                <span className="text-meta text-destructive">{order.error.message}</span>
              ) : null}
              <EmailCorrection
                key={order.id}
                row={order}
                canPurchase={canPurchase}
                busy={correctingEmail}
                onCorrect={(email, onDone) => {
                  onCorrectEmail(order.id, email, onDone);
                }}
              />
            </div>
            <OrderStatusChip status={order.status} />
            <IconButton
              icon="close"
              variant="outline"
              onClick={onClose}
              title={t('mice.drawer.close')}
              aria-label={t('mice.drawer.close')}
            />
          </div>
          <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-5 py-5">
            {profile}
            {loading ? <Spinner size="lg" /> : null}
            {error ? <EmptyHint density="compact">{t('mice.drawer.loadError')}</EmptyHint> : null}
            {!loading && !error && vouchers.length === 0 ? (
              <p className="text-meta text-muted-foreground">{t('mice.drawer.noVoucher')}</p>
            ) : null}
            {vouchers.map((voucher) => (
              <section key={voucher.id} className="flex flex-col gap-3">
                <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
                  {blockTitle(order, voucher.id)}
                </span>
                <span className="font-mono text-card-id text-foreground">
                  {voucher.voucherCode ?? '—'}
                </span>
                <VoucherDetailSections voucher={voucher} />
              </section>
            ))}
            {otherLive.length > 0 ? (
              <section className="flex flex-col gap-3 border-t border-border-soft pt-5">
                <span className="font-mono text-tiny font-medium uppercase tracking-eyebrow-wide text-faint-foreground">
                  {t('mice.drawer.otherLive')}
                </span>
                {otherLive.map((voucher) => (
                  <div key={voucher.id} className="flex flex-col gap-3">
                    <span className="font-mono text-card-id text-foreground">
                      {voucher.voucherCode ?? '—'}
                    </span>
                    <VoucherDetailSections voucher={voucher} />
                  </div>
                ))}
              </section>
            ) : null}
          </div>
        </>
      ) : null}
    </Drawer>
  );
}
